/** /ext-ids (+ /ext-ids/:id for the detail drawer)
 *
 * Restech external-ID mappings across tenants, driven by the reusable
 * QuerySearch query-builder widget. Rows come from the `demo_ext_id_index`
 * view, which unions the four places an ID can live — so ids are COMPOSITE
 * STRINGS (`aeid:1234`, `tour:56`), never integers. Server-side pagination +
 * sorting; all column filtering is delegated to the widget. Small table:
 * searches may be unbounded — no mandatory created window.
 *
 * Batch ops (manage perm): delete, product matching / vendor lookups (the two
 * flags, named for what they do), manage tags, and manage fan-out tours. Rows
 * that cannot take an op — an entity's own external ID has no flags, no tags
 * and nothing to delete; an import-owned (managed) alias refuses every change
 * until it is taken over — come back as per-row skips rather than aborting the
 * run.
 *
 * A managed alias is read-only here: the row menu offers "Take over…" instead of
 * Edit and Delete; an ops-owned alias offers "Hand back to import…".
 *
 * The page is organised around the external ID rather than the tour, because
 * several large tenants map almost entirely through aliases (citywonders: 2
 * primaries, 1545 aliases) and a few have no primary IDs at all.
 */
import AddIcon from '@mui/icons-material/Add';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SearchIcon from '@mui/icons-material/Search';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as extIdsApi from '@/api/extIds';
import type { ExtIdListItem } from '@/api/extIds';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { isStatementTimeout } from '@/api/client';
import { deleteBatchAction, type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { formatTenantIds, singleTenantId } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { ManageTagsDialog } from '@/features/bookings/ManageTagsDialog';
import { pageTenantsFrom } from '@/scope/pageTenants';

import { messageOf } from './extIdErrors';
import { ExtIdCreateDialog } from './ExtIdCreateDialog';
import {
  HAND_BACK_LABEL,
  MANAGED_DELETE_REASON,
  MANAGED_EDIT_REASON,
  TAKE_OVER_LABEL,
  ownershipActionOf,
} from './ownership';
import { useExtIdOwnership } from './useExtIdOwnership';
import { ExtIdDetailDrawer } from './ExtIdDetailDrawer';
import { ManageTargetsBatchDialog } from './ManageTargetsBatchDialog';
import { SetFlagBatchDialog } from './SetFlagBatchDialog';
import { buildColumns } from './extIdsColumns';
import { buildExtIdNamespaces } from './extIdNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN: string[] = [];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize). */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function ExtIdsPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: drawerId } = useParams<{ id?: string }>();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildExtIdNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;
  const perms = usePermissions();
  const canManage = perms.hasPerm('console_ext_ids_manage');

  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ el: HTMLElement; row: ExtIdListItem } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ExtIdListItem | null>(null);
  const ownership = useExtIdOwnership();
  // ⌘K "Create new external ID mapping" quick-link (`?new`).
  useCreateIntent(useCallback(() => setCreateOpen(true), []));

  const presets = useGridPresets({
    grid: 'ext_ids',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          showTenant: defaultShowTenant,
          onQuickFilter: () => undefined,
          detailPath: (id) => `/ext-ids/${id}`,
        }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'value',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= already names a tenant; the ambient console
  // scope then stands down rather than AND-ing a second, conflicting one.
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;
  // The single-tenant predicate (D5): the batch dialogs' tenant (tags, fan-out
  // tours) comes from the page only when it names exactly one operator.
  const singleTenant = singleTenantId(tenantApiValue) === null ? null : (tenants[0] ?? null);
  // The create dialogs feature the page's effective tenants and pre-select the
  // one when there is exactly one (D9) — none while a `tenant=` condition in ?q=
  // has the scope stand down.
  const pageTenants = pageTenantsFrom(tenantApiValue, tenants);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const onTenantChange = pageTenant.setTenants;


  const detailPath = useCallback(
    (id: string) => `/ext-ids/${encodeURIComponent(id)}${location.search}`,
    [location.search],
  );

  /** Same route with `edit=1`, so "Edit" from the kebab lands straight in the
   *  form and the state stays shareable like every other panel here. */
  const editPath = useCallback(
    (id: string) => {
      const next = new URLSearchParams(location.search);
      next.set('edit', '1');
      return `/ext-ids/${encodeURIComponent(id)}?${next.toString()}`;
    },
    [location.search],
  );

  const startEditing = sp.get('edit') === '1';

  /** Drop `edit` once the form is left, so a copied link does not reopen it. */
  const exitEditUrl = useCallback(() => {
    if (!sp.get('edit')) return;
    const next = new URLSearchParams(sp);
    next.delete('edit');
    setSp(next, { replace: true });
  }, [sp, setSp]);

  const removeMutation = useMutation({
    mutationFn: (row: ExtIdListItem) => extIdsApi.remove(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show('Mapping deleted.', 'success');
      setConfirmDelete(null);
    },
    onError: (e) => {
      setConfirmDelete(null);
      // A managed refusal means the row changed owner since the page loaded:
      // refetch so the grid shows it as the server does.
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
      snackbar.show(messageOf(e, 'Could not delete the mapping.'), 'error');
    },
  });

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'ext_ids' });
  const query = useQuery({
    queryKey: [
      'ext-ids',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
    ],
    queryFn: () =>
      extIdsApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  const total = query.data && query.data.count >= 0 ? query.data.count : rows.length;

  // Quick filters from cell popovers: append an equality chip for the namespace.
  // The tenant goes through the picker instead, REPLACING its tenants (its chip
  // would be stripped right back out). The Tenant cell hands over the operator's
  // code, which names nothing in `?tenant=`: the row on screen gives its id.
  const { setRaw: setPageTenant } = pageTenant;
  // Read at click time, so a data refresh doesn't rebuild the columns.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        const id = /^\d+$/.test(value)
          ? Number(value)
          : rowsRef.current.find((r) => r.tenant?.code === value)?.tenant?.id;
        if (id !== undefined) {
          setPageTenant(String(id));
          return;
        }
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery, setPageTenant],
  );

  // When a used_by_* filter is active the server narrows the page in Python,
  // so `count` describes the pre-facet superset. Say so rather than letting
  // the footer contradict the rows.
  const facetFiltered = query.data?.facet_filtered === true;

  // ---- batch -------------------------------------------------------------
  // Raw DataGridPro, so the batch layer is wired by hand. Row ids are COMPOSITE
  // STRINGS ('aeid:1234') — String(), never Number().
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      setExtendedTimeout(false);
      // "All matching" means the OLD query's set; keeping it across a query
      // change would act on rows the operator can no longer see.
      setAllMatching(false);
    }
  }, [queryIdentity]);

  const selectedIds = useMemo(() => selectionModel.map((i) => String(i)), [selectionModel]);
  const selectedCount = allMatching ? total : selectedIds.length;
  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const filterQuery = useMemo(() => {
    // Must reproduce the list query exactly, or `all_matching` resolves a
    // different set than the grid shows.
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'ext_ids',
    queryKey: ['ext-ids'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: unknown,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) =>
      batchAction.run({
        action,
        label,
        selection: selection as never,
        ...(params ? { params } : {}),
        refusals,
        filterQuery: pinnedQuery ?? filterQuery,
      }),
    [batchAction, filterQuery],
  );

  const selectedRowTenantIds = useMemo(
    () => rows.filter((r) => selectedIds.includes(r.id)).map((r) => r.tenant?.id ?? null),
    [rows, selectedIds],
  );

  // Declared unconditionally: gating the list on a page-level permission check
  // removes the whole checkbox column and reads as "batch not supported".
  // BatchActionsMenu filters per-action, and the backend re-checks.
  const batchActions: BatchActionDef[] = useMemo(
    () => [
      {
        key: 'set_matching',
        label: 'Product matching…',
        icon: <SearchIcon fontSize="small" />,
        permission: 'console_ext_ids_manage',
        action: 'set_matching',
        dialog: (ctx) => <SetFlagBatchDialog ctx={ctx} kind="matching" />,
      },
      {
        key: 'set_queryable',
        label: 'Vendor lookups…',
        icon: <LinkOffIcon fontSize="small" />,
        permission: 'console_ext_ids_manage',
        action: 'set_queryable',
        dialog: (ctx) => <SetFlagBatchDialog ctx={ctx} kind="queryable" />,
      },
      {
        key: 'manage_tags',
        label: 'Manage tags…',
        icon: <LocalOfferIcon fontSize="small" />,
        permission: 'console_ext_ids_manage',
        action: 'manage_tags',
        dialog: (ctx) => (
          <ManageTagsDialog
            ctx={ctx}
            gridTenantId={singleTenant?.id ?? null}
            selectedRowTenantIds={selectedRowTenantIds}
            noun="mapping"
            // A mapping's tags are read at match time, never cached, so there
            // is no side-effect chain to offer a refresh for.
            showSideEffects={false}
          />
        ),
      },
      {
        key: 'manage_targets',
        label: 'Manage fan-out tours…',
        icon: <CallSplitIcon fontSize="small" />,
        permission: 'console_ext_ids_manage',
        action: 'manage_targets',
        dialog: (ctx) => <ManageTargetsBatchDialog ctx={ctx} tenant={singleTenant} />,
      },
      deleteBatchAction('console_ext_ids_manage'),
    ],
    [singleTenant, selectedRowTenantIds],
  );

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({
          showTenant: tenants.length !== 1,
          onQuickFilter,
          detailPath,
          // No manage permission => no kebab at all, rather than a menu whose
          // every item is disabled.
          ...(canManage
            ? { onRowMenu: (el: HTMLElement, row: ExtIdListItem) => setRowMenu({ el, row }) }
            : {}),
        }),
        presets.orderedFields,
      ),
    [tenants.length, onQuickFilter, detailPath, presets.orderedFields, canManage],
  );

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="ext-ids"
        title="External ID mappings"
        subtitle="Every restech product ID mapped to a tour, itinerary or activity — across all tenants."
        actions={
          <PermissionGate perm="console_ext_ids_manage">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={!canManage}
            >
              New mapping
            </Button>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search mappings…  e.g. kind=alias & used_by_bookings=no"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {facetFiltered && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A “used by” filter is active. Those are worked out per row rather than in the database,
          so the total below counts everything matching the rest of your search — only the rows
          shown have been narrowed.
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<ExtIdListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={total}
          // Composite string ids — never Number() them.
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          onRowClick={(p) => navigate(detailPath(String(p.row.id)))}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          disableColumnFilter
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch
                  ? 'No mappings match this search.'
                  : 'No external ID mappings yet.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'ext-ids',
              presets: presets.toolbar,
              batch: {
                actions: batchActions,
                ids: selectedIds,
                allMatching,
                count: selectedCount,
                matched: total,
                filterQuery,
                runStandard,
                clearSelection,
                onSelectAllMatching: () => setAllMatching(true),
                isPending: batchAction.isPending,
              },
            } as GridToolbarProps,
          }}
        />
      )}

      <Menu anchorEl={rowMenu?.el ?? null} open={rowMenu !== null} onClose={() => setRowMenu(null)}>
        {rowMenu?.row.managed ? (
          // An import keeps a managed alias in step; the server refuses any edit
          // that does not take it over first.
          <Tooltip title={MANAGED_EDIT_REASON}>
            <span>
              <MenuItem disabled>Edit…</MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem
            onClick={() => {
              const row = rowMenu?.row;
              setRowMenu(null);
              if (row) navigate(editPath(row.id));
            }}
          >
            Edit…
          </MenuItem>
        )}
        {rowMenu && ownershipActionOf(rowMenu.row) && (
          <MenuItem
            onClick={() => {
              const row = rowMenu?.row;
              setRowMenu(null);
              if (row) ownership.request(row);
            }}
          >
            {ownershipActionOf(rowMenu.row) === 'take_over' ? TAKE_OVER_LABEL : HAND_BACK_LABEL}
          </MenuItem>
        )}
        {rowMenu?.row.kind === 'primary' || rowMenu?.row.managed ? (
          // A primary mapping is a column on a live entity; "delete" would mean
          // blanking it, which belongs on the entity itself. A managed alias would
          // only be recreated by its import's next sync.
          <Tooltip
            title={
              rowMenu?.row.managed
                ? MANAGED_DELETE_REASON
                : "This is the entity's own external ID — clear it on the tour, itinerary or activity."
            }
          >
            <span>
              <MenuItem disabled>Delete</MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem
            onClick={() => {
              const row = rowMenu?.row;
              setRowMenu(null);
              if (row) setConfirmDelete(row);
            }}
          >
            Delete…
          </MenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete this mapping?"
        message={
          confirmDelete
            ? `“${confirmDelete.value}” will stop matching bookings to ${
                confirmDelete.target.name ?? 'its tour'
              }.` +
              ' Any tags it applies, and any further tours it books onto, go with it.'
            : ''
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDelete && removeMutation.mutate(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />

      {ownership.dialog}

      {drawerId && (
        <ExtIdDetailDrawer
          extId={decodeURIComponent(drawerId)}
          open
          startEditing={startEditing}
          onEditingDone={exitEditUrl}
          onClose={() => {
            exitEditUrl();
            navigate(`/ext-ids${location.search}`);
          }}
          detailPath={detailPath}
        />
      )}

      <ExtIdCreateDialog
        open={createOpen}
        pageTenants={pageTenants}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => navigate(detailPath(id))}
      />
    </Box>
  );
}
